/**
 * components/Settings/AI/AISettingsCard.tsx
 * Display card for current AI provider configuration
 *
 * Shows provider name, default model, masked API key (last 4 chars),
 * validation status badge, and last-validated timestamp. All management
 * actions are delegated to the parent via callback props.
 *
 * Work Unit: WU02 - AI Settings Management
 * Task: T01 - AI Settings Card
 * Sprint: S02 - AI Setup & Settings UI
 * Project: P09 - AI Integration
 */

// 1. External dependencies
import React, { useEffect, useRef, useState } from 'react';
import { Bot, KeyRound, RefreshCw, Settings2, Trash2 } from 'lucide-react';

// 2. Workspace packages
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Skeleton,
} from '@enfomentis/ui';

// 3. Internal absolute imports
import type { AICredential, AIProviderType, AIValidationStatus } from '@/types/shared/AI';
import {
  AI_DEFAULT_MODEL_MAP,
  AI_MODELS,
  AI_PROVIDER_DISPLAY_MAP,
  AI_VALIDATION_STATUS_DISPLAY_MAP,
} from '@/constants/ai';
import { aiCredentialService } from '@/services/AICredentialService';

// ─── Constants ──────────────────────────────────────────────────────────────

/** Chip color mapping per validation status */
const STATUS_CHIP_COLOR_MAP: Readonly<Record<AIValidationStatus, 'success' | 'danger' | 'default' | 'warning'>> = {
  error: 'warning',
  invalid: 'danger',
  untested: 'default',
  valid: 'success',
};

/** Divisors for relative time formatting (seconds → minutes → hours → days) */
const RELATIVE_TIME_DIVISIONS: readonly { amount: number; unit: Intl.RelativeTimeFormatUnit }[] = [
  { amount: 60, unit: 'seconds' },
  { amount: 60, unit: 'minutes' },
  { amount: 24, unit: 'hours' },
  { amount: 365, unit: 'days' },
];

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Format an ISO timestamp as a relative time string (e.g., "2 hours ago")
 *
 * @param isoTimestamp - ISO 8601 timestamp string
 * @returns Human-readable relative time string
 */
function formatRelativeTime(isoTimestamp: string): string {
  const formatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  let duration = (new Date(isoTimestamp).getTime() - Date.now()) / 1000;

  for (const division of RELATIVE_TIME_DIVISIONS) {
    if (Math.abs(duration) < division.amount) {
      return formatter.format(Math.round(duration), division.unit);
    }
    duration /= division.amount;
  }

  return formatter.format(Math.round(duration), 'years');
}

/**
 * Get the display name for a model ID
 *
 * @param modelId - Provider-specific model identifier
 * @returns Human-readable model name, or the raw ID as fallback
 */
function getModelDisplayName(modelId: string): string {
  const model = AI_MODELS.find((m) => m.id === modelId);
  return model?.name ?? modelId;
}

// ─── Props ──────────────────────────────────────────────────────────────────

/**
 * Props for the AI Settings Card
 *
 * @see AISettingsCard
 */
export interface AISettingsCardProps {
  /** Called when the user clicks "Update Key" */
  onUpdateKey: () => void;

  /** Called when the user clicks "Re-validate" */
  onRevalidate: () => void;

  /** Called when the user clicks "Switch Provider" */
  onSwitchProvider: () => void;

  /** Called when the user clicks "Remove" */
  onRemove: () => void;
}

// ─── Types ──────────────────────────────────────────────────────────────────

/** Internal state for loaded credential data */
interface CredentialDisplayData {
  provider: AIProviderType;
  providerName: string;
  modelName: string;
  maskedKey: string;
  validationStatus: AIValidationStatus;
  validatedAt: string | undefined;
}

// ─── Component ──────────────────────────────────────────────────────────────

/**
 * AI Settings Card
 *
 * Displays current AI configuration with provider info, masked key,
 * validation status, and management action buttons.
 *
 * Data is loaded from IndexedDB on mount via `aiCredentialService`.
 * All management actions are delegated to the parent via callback props.
 *
 * @example
 * ```tsx
 * <AISettingsCard
 *   onUpdateKey={() => setMode('update')}
 *   onRevalidate={handleRevalidate}
 *   onSwitchProvider={() => setMode('wizard')}
 *   onRemove={handleRemove}
 * />
 * ```
 */
export const AISettingsCard: React.FC<AISettingsCardProps> = ({
  onUpdateKey,
  onRevalidate,
  onSwitchProvider,
  onRemove,
}) => {
  // ─── State ────────────────────────────────────────────────────────────

  const [isLoading, setIsLoading] = useState(true);
  const [displayData, setDisplayData] = useState<CredentialDisplayData | null>(null);

  // ─── Unmount Safety ───────────────────────────────────────────────────

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ─── Data Loading ─────────────────────────────────────────────────────

  useEffect(() => {
    const loadCredentialData = async (): Promise<void> => {
      try {
        const providers = await aiCredentialService.listProviders();
        if (!mountedRef.current) return;

        const provider = providers[0];
        if (!provider) {
          setIsLoading(false);
          return;
        }

        // Load credential metadata and decrypted key in parallel
        const [credential, decryptedKey] = await Promise.all([
          aiCredentialService.getCredential(provider),
          aiCredentialService.getDecryptedKey(provider),
        ]);

        if (!mountedRef.current) return;

        if (!credential) {
          setIsLoading(false);
          return;
        }

        const defaultModelId = AI_DEFAULT_MODEL_MAP[provider];

        setDisplayData({
          provider,
          providerName: AI_PROVIDER_DISPLAY_MAP[provider],
          modelName: getModelDisplayName(defaultModelId),
          maskedKey: decryptedKey
            ? `••••••${decryptedKey.slice(-4)}`
            : '••••••••',
          validationStatus: credential.validationStatus,
          validatedAt: (credential as AICredential).validatedAt,
        });
      } finally {
        if (mountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    void loadCredentialData();
  }, []);

  // ─── Loading Skeleton ─────────────────────────────────────────────────

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader className="flex items-center gap-3 p-4 sm:p-6 pb-0 sm:pb-0">
          <Skeleton className="size-5 rounded" />
          <Skeleton className="h-5 w-40 rounded" />
        </CardHeader>
        <CardBody className="space-y-4 p-4 sm:p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Skeleton className="h-12 rounded-lg" />
            <Skeleton className="h-12 rounded-lg" />
            <Skeleton className="h-12 rounded-lg" />
            <Skeleton className="h-12 rounded-lg" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-9 w-24 rounded-lg" />
            <Skeleton className="h-9 w-24 rounded-lg" />
          </div>
        </CardBody>
      </Card>
    );
  }

  // ─── No Data ──────────────────────────────────────────────────────────

  if (!displayData) {
    return null;
  }

  // ─── Main Render ──────────────────────────────────────────────────────

  const statusLabel = AI_VALIDATION_STATUS_DISPLAY_MAP[displayData.validationStatus];
  const statusColor = STATUS_CHIP_COLOR_MAP[displayData.validationStatus];

  return (
    <Card className="w-full" aria-label="AI Configuration">
      {/* Header: Icon + Title + Status Chip */}
      <CardHeader className="flex items-center gap-3 p-4 pb-0 sm:p-6 sm:pb-0">
        <Bot className="size-5 text-primary" />
        <h3 className="text-lg font-semibold">AI Configuration</h3>
        <Chip size="sm" color={statusColor} variant="flat" className="ml-auto">
          {statusLabel}
        </Chip>
      </CardHeader>

      <CardBody className="space-y-5 p-4 sm:p-6">
        {/* Configuration Details Grid */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* Provider */}
          <div className="space-y-1">
            <p className="text-xs text-default-400">Provider</p>
            <p className="text-sm font-medium">{displayData.providerName}</p>
          </div>

          {/* Model */}
          <div className="space-y-1">
            <p className="text-xs text-default-400">Model</p>
            <p className="text-sm font-medium">{displayData.modelName}</p>
          </div>

          {/* API Key */}
          <div className="space-y-1">
            <p className="text-xs text-default-400">API Key</p>
            <p className="text-sm font-medium font-mono">{displayData.maskedKey}</p>
          </div>

          {/* Last Validated */}
          <div className="space-y-1">
            <p className="text-xs text-default-400">Last Validated</p>
            <p className="text-sm font-medium">
              {displayData.validatedAt
                ? formatRelativeTime(displayData.validatedAt)
                : 'Never'}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="flat"
            startContent={<KeyRound className="size-3.5" />}
            onPress={onUpdateKey}
          >
            Update Key
          </Button>
          <Button
            size="sm"
            variant="flat"
            startContent={<RefreshCw className="size-3.5" />}
            onPress={onRevalidate}
          >
            Re-validate
          </Button>
          <Button
            size="sm"
            variant="flat"
            startContent={<Settings2 className="size-3.5" />}
            onPress={onSwitchProvider}
          >
            Switch Provider
          </Button>
          <Button
            size="sm"
            variant="flat"
            color="danger"
            startContent={<Trash2 className="size-3.5" />}
            onPress={onRemove}
          >
            Remove
          </Button>
        </div>
      </CardBody>
    </Card>
  );
};
